import { getLong, setLong, type Storage } from '../platform/storage'
import type { Phase, PhaseType, WakeProgram } from '../core/types'
import { KEY } from './keys'

/**
 * Правки «Моего дня»: свой список фаз поверх рекомендованной программы, отдельно для каждой программы.
 * Формат JSON тот же, что пишет Android (`DayStore`), поэтому правки переживают синхронизацию с часами.
 */

export interface StoredPhase {
  title: string
  minutes: number
  type: string
  tip?: string
  flexible?: boolean
  /** В Android поле называется `tired`. */
  tired?: boolean
}

const TYPES: PhaseType[] = ['FEED', 'CARE', 'ACTIVE', 'CALM', 'SLEEP']

/** Тот же формат — для списков ВБ (`stores/schedules.ts`). */
export function fromStored(raw: StoredPhase): Phase | null {
  return toPhase(raw)
}

export function toStored(p: Phase): StoredPhase {
  return { title: p.title, minutes: p.minutes, type: p.type, tip: p.tip, flexible: p.flexible, tired: p.showTiredSigns }
}

function toPhase(raw: StoredPhase): Phase | null {
  if (typeof raw?.title !== 'string' || typeof raw?.minutes !== 'number') return null
  if (!TYPES.includes(raw.type as PhaseType)) return null
  return {
    title: raw.title,
    minutes: raw.minutes,
    type: raw.type as PhaseType,
    tip: raw.tip ?? '',
    flexible: raw.flexible ?? true,
    showTiredSigns: raw.tired ?? false,
  }
}

export async function loadCustomPhases(storage: Storage, programId: string): Promise<Phase[] | null> {
  // По частям: длинный список фаз не влезает в одну ячейку, а с сервера он приходит именно так
  const raw = await getLong(storage, KEY.customPhases(programId))
  if (raw === null) return null
  try {
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return null
    const phases = parsed.map(toPhase)
    if (phases.length === 0 || phases.some((p) => p === null)) return null
    return phases as Phase[]
  } catch {
    return null // повреждённые данные не должны ронять приложение
  }
}

export async function saveCustomPhases(
  storage: Storage,
  programId: string,
  phases: Phase[],
  now = Date.now(),
): Promise<void> {
  const stored: StoredPhase[] = phases.map((p) => ({
    title: p.title,
    minutes: p.minutes,
    type: p.type,
    tip: p.tip,
    flexible: p.flexible,
    tired: p.showTiredSigns,
  }))
  await setLong(storage, KEY.customPhases(programId), JSON.stringify(stored))
  await storage.set(KEY.phasesUpdatedAt(programId), String(now))
}

export async function clearCustomPhases(storage: Storage, programId: string, now = Date.now()): Promise<void> {
  // Через setLong: иначе хвосты длинного значения остались бы лежать
  await setLong(storage, KEY.customPhases(programId), '')
  await storage.remove(KEY.customPhases(programId))
  await storage.set(KEY.phasesUpdatedAt(programId), String(now))
}

export async function isCustom(storage: Storage, programId: string): Promise<boolean> {
  return (await getLong(storage, KEY.customPhases(programId))) !== null
}

export async function phasesUpdatedAt(storage: Storage, programId: string): Promise<number> {
  return Number((await storage.get(KEY.phasesUpdatedAt(programId))) ?? 0)
}

/** Рекомендованная программа с наложенными правками пользователя. */
export async function applyCustomPhases(storage: Storage, base: WakeProgram): Promise<WakeProgram> {
  const phases = await loadCustomPhases(storage, base.id)
  return phases === null ? base : { ...base, phases }
}
